import { useEffect, useState } from "react";
import type { ContentEntry } from "@lexireveal/content-schema";
import { chargerCorpus } from "./corpus";
import type { Controles } from "./controles";

/** Le corpus publié, chargé une fois, pour les couples confondables. */
export function useCorpus(): readonly ContentEntry[] | null {
  const [corpus, setCorpus] = useState<readonly ContentEntry[] | null>(null);
  useEffect(() => {
    let actuel = true;
    // Sans corpus, les autres contrôles tournent quand même : seul le test
    // des couples attend.
    chargerCorpus()
      .then((c) => actuel && setCorpus(c))
      .catch(() => undefined);
    return () => {
      actuel = false;
    };
  }, []);
  return corpus;
}

/**
 * Les contrôles éditoriaux, en direct. Les échecs bloquent la publication ;
 * les signaux « à juger » exigent que l'administrateur coche qu'il les a vus.
 */
export function PanneauControles(props: {
  readonly controles: Controles;
  readonly corpusCharge: boolean;
  readonly confirme: boolean;
  readonly onConfirme: (valeur: boolean) => void;
}) {
  const { controles } = props;
  const aJuger = controles.signaux.length > 0 || controles.couples.length > 0;

  return (
    <section className="controles" aria-label="Contrôles éditoriaux">
      <h3>Contrôles éditoriaux</h3>
      <p className={controles.echecs.length === 0 ? "succes" : "erreur"}>
        {controles.nombreDeMots} mot{controles.nombreDeMots > 1 ? "s" : ""} ·{" "}
        {controles.echecs.length === 0 ? "aucune règle cassée" : `${controles.echecs.length} règle(s) cassée(s)`}
      </p>
      {controles.echecs.length > 0 && (
        <ul aria-label="Échecs" className="liste-echecs">
          {controles.echecs.map((echec) => (
            <li key={echec}>{echec}</li>
          ))}
        </ul>
      )}

      {aJuger && (
        <div className="a-juger">
          <h4>À juger : le jeu deviendrait-il trop simple ?</h4>
          <ul aria-label="Signaux à juger">
            {controles.signaux.map((signal) => (
              <li key={signal.code}>La définition {signal.message}.</li>
            ))}
            {controles.couples.map((couple) => (
              <li key={couple}>Couple confondable, même longueur : {couple}. Un joueur de bonne foi pourrait-il proposer l'autre mot ?</li>
            ))}
          </ul>
          <label className="case">
            <input type="checkbox" checked={props.confirme} onChange={(e) => props.onConfirme(e.target.checked)} />
            J'ai vu ces signaux : la définition reste juste et assez difficile
          </label>
        </div>
      )}
      {!props.corpusCharge && <p className="discret">Recherche des couples confondables : chargement du corpus…</p>}

      {controles.difficulteEstimee !== null && (
        <details>
          <summary>Difficulté estimée d'après la structure du mot : {controles.difficulteEstimee.note} / 5</summary>
          <ul>
            {controles.difficulteEstimee.raisons.map((raison) => (
              <li key={raison}>{raison}</li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}
